import React, { useCallback, useEffect } from "react";
import { useLenis } from "@/lib/useLenis";
import { useDocumentHead } from "@/hooks/useDocumentHead";

import { HeroSection } from "@/components/sections/HeroSection";
import { SelectedWorkSection } from "@/components/sections/SelectedWorkSection";
import { AudienceSection } from "@/components/sections/AudienceSection";
import { ServicesSection } from "@/components/sections/ServicesSection";
import { InteractiveQuestionsSection } from "@/components/sections/InteractiveQuestionsSection";
import { BuiltForSection } from "@/components/sections/BuiltForSection";
import { FeatureCardsSection } from "@/components/sections/FeatureCardsSection";
import { StoriesInMotionSection } from "@/components/sections/StoriesInMotionSection";
import { ProcessSection } from "@/components/sections/ProcessSection";
import { FaqSection } from "@/components/sections/FaqSection";
import { AboutSection } from "@/components/sections/AboutSection";
import { ContactSection } from "@/components/sections/ContactSection";
import { FooterSection } from "@/components/sections/FooterSection";
import { NavBar } from "@/components/atoms/NavBar";

export default function HomePage({ initialScrollTarget }) {
    useLenis();

    useDocumentHead({
        title: initialScrollTarget === "contact"
            ? "Contact GrowCo Motion | Book a Motion Design Demo"
            : "Motion Design & Explainer Video Studio India | GrowCo Motion",
        description: initialScrollTarget === "contact"
            ? "Book a 30-minute discovery call with GrowCo Motion to scope your explainer video, product animation or launch film."
            : "GrowCo Motion is a motion design studio in India creating explainer videos, product animations and launch films that make complex ideas clear.",
        canonical: initialScrollTarget === "contact"
            ? "https://growcomotion.com/contact"
            : "https://growcomotion.com/",
    });

    const scrollToContact = useCallback(() => {
        const el = document.getElementById("contact");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    useEffect(() => {
        if (initialScrollTarget === "contact") {
            // Delay so the page lays out before scrolling
            const t = setTimeout(() => {
                const el = document.getElementById("contact");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 400);
            return () => clearTimeout(t);
        }
    }, [initialScrollTarget]);

    return (
        <>
            {/* SEO-only h1 for crawlers (visually hidden but readable). The visual hero
                keeps "Ideas Deserve Motion" as the brand line. */}
            <h1
                style={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: "hidden",
                    clip: "rect(0,0,0,0)",
                    whiteSpace: "nowrap",
                    border: 0,
                }}
            >
                Motion Design Studio for Explainer Videos &amp; Product Animation
            </h1>
            <NavBar />
            <main className="relative w-full overflow-x-clip" style={{ background: "var(--bg)" }}>
                <div className="flex flex-col gap-16 sm:gap-20 md:gap-24 py-6 md:py-8">
                    <HeroSection onCta={scrollToContact} />
                    <div id="work" style={{ scrollMarginTop: "88px" }}>
                        <SelectedWorkSection />
                    </div>
                    <div id="about" style={{ scrollMarginTop: "88px" }}>
                        <AboutSection />
                    </div>
                    <AudienceSection />
                    <div id="services" style={{ scrollMarginTop: "88px" }}>
                        <ServicesSection />
                    </div>
                    <InteractiveQuestionsSection />
                    <BuiltForSection />
                    <FeatureCardsSection />
                    <div id="stories" style={{ scrollMarginTop: "88px" }}>
                        <StoriesInMotionSection />
                    </div>
                    <div id="process" style={{ scrollMarginTop: "88px" }}>
                        <ProcessSection />
                    </div>
                    <div id="faq" style={{ scrollMarginTop: "88px" }}>
                        <FaqSection />
                    </div>
                    <div id="contact" style={{ scrollMarginTop: "88px" }}>
                        <ContactSection />
                    </div>
                    <FooterSection />
                </div>
            </main>
        </>
    );
}
